import {
	clearProgressiveModules,
	selectCurrentModules,
} from "@features/setupAssistant/setupAssistantSlice";
import { useAppDispatch, useAppSelector, usePermissions } from "@app/hooks";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import {
	selectCurrentProject,
	selectTemporaryMapData,
	setTemporaryMapData,
	clearTemporaryMapData,
	selectProjectAreaDefined
} from "@features/project/projectSlice";
import { useUploadMapDataMutation } from "@features/project/projectApiSlice";
import { useMapCommunication } from "./useMapCommunication";
import { useHandleProjectState } from "@pages/project/useHandleProjectState";
import ClimateIdentifier, { ClimateIdentifiers } from "./ClimateIdentifier";
import MapIframe from "./MapIframe";
import MapGuide from "./MapGuide";
import MapButtons from "./MapButtons";
import TranslatableText from "../../translations/TranslatableText";
import ConfirmationModal from "@components/modal/ConfirmationModal";
import useErrorMessage from "@utils/useErrorMessage";
import FadeAnimation from "@components/animations/FadeAnimation";
import { FeatureCollection } from "./mapMessages";
import { Project } from "@typedefs/interfaces";

// Types
interface MapActionProps {
	style?: React.CSSProperties;
	isMapActive?: boolean;
}

// Custom hooks
const useMapModals = () => {
	const [showClearAreaModal, setShowClearAreaModal] = useState(false);
	const [showOverwriteAreaModal, setShowOverwriteAreaModal] = useState(false);

	return {
		showClearAreaModal,
		showOverwriteAreaModal,
		openClearAreaModal: () => setShowClearAreaModal(true),
		closeClearAreaModal: () => setShowClearAreaModal(false),
		openOverwriteAreaModal: () => setShowOverwriteAreaModal(true),
		closeOverwriteAreaModal: () => setShowOverwriteAreaModal(false),
	};
};

const useMapDataOperations = ({
	project,
	isNewProject,
	areaToSave,
	setAreaToSave,
	clearMap,
}: {
	project?: Project | null,
	isNewProject: boolean,
	areaToSave: FeatureCollection | null,
	setAreaToSave: (area: FeatureCollection | null) => void,
	clearMap: () => void
}) => {
	const dispatch = useAppDispatch();
	const [uploadMapData, { isLoading: isUploadingMapData, isError, error }] = useUploadMapDataMutation();
	const { errorMsg } = useErrorMessage({ isError, error });
	const temporaryMapData = useAppSelector(selectTemporaryMapData);

	// Auto-save map data when creating a new project
	useEffect(() => {
		if (isNewProject && areaToSave && !temporaryMapData) {
			dispatch(setTemporaryMapData(areaToSave));
			setAreaToSave(null);
		}
	}, [isNewProject, areaToSave, temporaryMapData, dispatch, setAreaToSave]);

	const saveMapData = async () => {
		if (!areaToSave) return;

		if (isNewProject) {
			dispatch(setTemporaryMapData(areaToSave));
			clearMap();
			setAreaToSave(null);
			return;
		}

		if (project?.id && !isUploadingMapData) {
			await uploadMapData({ projectId: project.id, mapData: JSON.stringify(areaToSave) }).unwrap();
			clearMap();
			setAreaToSave(null);
		}
	};

	const clearMapData = async () => {
		if (isNewProject) {
			dispatch(clearTemporaryMapData());
			clearMap();
			setAreaToSave(null);
			return;
		}

		if (project?.id) {
			await uploadMapData({ projectId: project.id, mapData: null }).unwrap();
			clearMap();
			setAreaToSave(null);
		}
	};

	return {
		saveMapData,
		clearMapData,
		isUploadingMapData,
		errorMsg,
		temporaryMapData,
	};
};

const identifiersInitialValues: ClimateIdentifiers = {
	mat: null,
	map: null,
	pet: null,
	elevation: null
};

const MapAction = ({ style, isMapActive }: MapActionProps) => {
	const dispatch = useAppDispatch();
	const modules = useAppSelector(selectCurrentModules);
	const location = useLocation();
	const projectAreaDefined = useAppSelector(selectProjectAreaDefined);
	const { tabsOpen } = useAppSelector(selectCurrentBuilder);
	const { project } = useAppSelector(selectCurrentProject);
	const { isPublic } = usePermissions();
	const { isNewProject } = useHandleProjectState();

	// State management
	const [messages, setMessages] = useState<string[]>([]);
	const {
		showClearAreaModal,
		showOverwriteAreaModal,
		openClearAreaModal,
		closeClearAreaModal,
		openOverwriteAreaModal,
		closeOverwriteAreaModal,
	} = useMapModals();

	const {
		iframeRef,
		clearMap,
		identifiers,
		areaToSave,
		setAreaToSave
	} = useMapCommunication({
		messagesState: [messages, setMessages],
		onError: (error) => console.error("Map communication error:", error),
		identifiersInitialValues
	});

	const projectHasNoMapData = useMemo<boolean>(
		() => project ? project.map_data === null : true,
		[project]
	);

	const {
		saveMapData,
		clearMapData,
		isUploadingMapData,
		errorMsg,
		temporaryMapData,
	} = useMapDataOperations({
		project,
		isNewProject,
		areaToSave,
		setAreaToSave,
		clearMap
	});

	// Handlers
	const handleClearAreaClick = openClearAreaModal;
	const handleSaveAreaClick = async () => {
		if (!areaToSave) return;

		if (projectHasNoMapData && !temporaryMapData) {
			await saveMapData();
		} else {
			openOverwriteAreaModal();
		}
	};

	// Clear progressive modules if not on new-activity path
	useEffect(() => {
		if (!location.pathname.endsWith("new-activity") && modules.length) {
			dispatch(clearProgressiveModules());
		}
	}, [location.pathname, modules.length, dispatch]);

	const clearMapDataAndCloseModal = async () => {
		await clearMapData();
		closeClearAreaModal();
	};

	const saveMapDataAndCloseModal = async () => {
		await saveMapData();
		closeOverwriteAreaModal();
	};

	return (
		<motion.section
			className={`w-100 h-100 p-2 bg-gray ${isMapActive ? "d-block" : "d-none"}`}
			initial={{ width: 0 }}
			animate={{ width: tabsOpen ? "inherit" : 0 }}
			exit={{ width: 0 }}
			style={style}
		>
			<div className="pos-relative mb-2 pb-1">
				<h2 className="fs-14 ff-medium-ext module-header w-fit">
					<TranslatableText translationKey="main.project_map" />
				</h2>
			</div>

			<MapGuide />

			<div style={{ position: "relative", zIndex: 1 }}>
				<MapIframe
					key={project?.id ?? "no-project"}
					iframeRef={iframeRef}
				/>
			</div>

			<span className="text-gray fs-12 d-block" style={{ maxWidth: '750px' }}>
				<TranslatableText translationKey="map.json_upload_not_supported" />
			</span>

			{!isPublic && !isNewProject && !projectAreaDefined && (
				<MapButtons
					projectMapData={isNewProject ? temporaryMapData : project?.map_data}
					areaToSave={areaToSave}
					onClearArea={handleClearAreaClick}
					onSaveArea={handleSaveAreaClick}
					isSaveLoading={isUploadingMapData}
				/>
			)}

			<FadeAnimation isShown={!!errorMsg} className="pt-1">
				<span className="text-error fs-14">{errorMsg}</span>
			</FadeAnimation>

			<div className="w-50">
				<ClimateIdentifier initialValues={identifiers} />
			</div>

			<ConfirmationModal
				open={showClearAreaModal}
				onCancel={closeClearAreaModal}
				onConfirm={clearMapDataAndCloseModal}
				message={<TranslatableText translationKey="map.confirm_delete_area" />}
				headerText="map.delete_area_header"
				confirmationMessage="main.delete"
				isLoading={isUploadingMapData}
			/>

			<ConfirmationModal
				open={showOverwriteAreaModal}
				onCancel={closeOverwriteAreaModal}
				onConfirm={saveMapDataAndCloseModal}
				message={<TranslatableText translationKey="map.confirm_overwrite_area" />}
				headerText="map.overwrite_area_header"
				confirmationMessage="main.save"
				isLoading={isUploadingMapData}
			/>
		</motion.section>
	);
};

export default MapAction;
